import { useForm, UseFormReturn, Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useState } from "react";
import { CustomerTier, FeeType } from "@/api.types";
import { useCustomerTiers } from "@/api/customers";
import {
  domesticBaseFeeSchema,
  dimensionalWeightFeeSchema,
  speedFeeSchema,
  specialHandlingFeeSchema,
  pickupHandlingFeeSchema,
  internationalBaseFeeSchema,
  internationalSurchargeFeeSchema,
  useCreateFee,
  DomesticBaseFeeCreateFormSchema,
  DimensionalWeightFeeCreateFormSchema,
  SpeedFeeCreateFormSchema,
  SpecialHandlingFeeCreateFormSchema,
  PickupHandlingFeeCreateFormSchema,
  InternationalBaseFeeCreateFormSchema,
  InternationalSurchargeFeeCreateFormSchema,
} from "@/api/pricings";
import { useServiceAreaByDepotId } from "@/api/service-areas";
import { useDepots } from "@/api/depots";

export function CreateFeeForm({ onSuccess }: { onSuccess?: () => void }) {
  const [selectedFeeType, setSelectedFeeType] =
    useState<FeeType>("domestic-base");

  // Create mutation hook
  const createFeeMutation = useCreateFee(selectedFeeType, {
    onSuccess: () => {
      onSuccess?.();
    },
  });

  // Update the query to handle the response structure
  const { data: customerTiersResponse } = useCustomerTiers();

  // Safely access the customer tiers array
  const customerTiers = customerTiersResponse?.data ?? [];

  // Form components for each fee type
  const DomesticBaseFeeForm = () => {
    const [selectedDepot, setSelectedDepot] = useState<string | null>(null);

    const { data: depots = [] } = useDepots();

    const { data: serviceAreasResponse } =
      useServiceAreaByDepotId(selectedDepot);

    // Safely access the nested data array
    const safeServiceAreas = serviceAreasResponse?.data ?? [];

    const form = useForm<DomesticBaseFeeCreateFormSchema>({
      resolver: zodResolver(domesticBaseFeeSchema),
      defaultValues: {
        isActive: true,
      },
    });

    const onSubmit = (data: DomesticBaseFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <Select
            onValueChange={setSelectedDepot}
            value={selectedDepot ?? undefined}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select depot" />
            </SelectTrigger>
            <SelectContent>
              {depots?.map((depot) => (
                <SelectItem key={depot.id} value={depot.id}>
                  {depot.name} (#{depot.depotNumber})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedDepot && (
            <>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="basePrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Base Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pickupServiceAreaId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pickup Service Area</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select pickup area" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {safeServiceAreas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name} ({area.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deliveryServiceAreaId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery Service Area</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select delivery area" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {safeServiceAreas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name} ({area.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>Active</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full">
                Create Domestic Base Fee
              </Button>
            </>
          )}
        </form>
      </Form>
    );
  };

  const DimensionalWeightFeeForm = () => {
    const form = useForm<DimensionalWeightFeeCreateFormSchema>({
      resolver: zodResolver(dimensionalWeightFeeSchema),
      defaultValues: {
        isActive: true,
        feeScope: "DOMESTIC",
      },
    });

    const onSubmit = (data: DimensionalWeightFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };
    console.log(form.formState.errors);

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="baseDimWeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base Dimensional Weight (kg)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="minWeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Min Weight (kg)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="maxWeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Weight (kg)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxSideLength"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Side Length (cm)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="factor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Factor</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pricePerKg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price per KG</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="fee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Base Fee</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="feeScope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Scope</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select fee scope" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="DOMESTIC">Domestic</SelectItem>
                    <SelectItem value="INTERNATIONAL">International</SelectItem>
                    <SelectItem value="ANY">Any</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between">
                <FormLabel>Active</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Create Dimensional Weight Fee
          </Button>
        </form>
      </Form>
    );
  };

  const SpeedFeeForm = () => {
    const [selectedDepot, setSelectedDepot] = useState<string | null>(null);
    const { data: depots = [] } = useDepots();
    const { data: serviceAreasResponse } =
      useServiceAreaByDepotId(selectedDepot);

    // Safely access the nested data array
    const safeServiceAreas = serviceAreasResponse?.data ?? [];

    const form = useForm<SpeedFeeCreateFormSchema>({
      resolver: zodResolver(speedFeeSchema),
      defaultValues: {
        isActive: true,
        feeScope: "DOMESTIC",
        minDeliveryTime: undefined,
        maxDeliveryTime: undefined,
        timeUnit: undefined,
      },
    });

    const onSubmit = (data: SpeedFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />

          <Select
            onValueChange={setSelectedDepot}
            value={selectedDepot ?? undefined}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select depot" />
            </SelectTrigger>
            <SelectContent>
              {depots?.map((depot) => (
                <SelectItem key={depot.id} value={depot.id}>
                  {depot.name} (#{depot.depotNumber})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedDepot && (
            <>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery Speed</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select speed type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="STANDARD">Standard</SelectItem>
                        <SelectItem value="EXPRESS">Express</SelectItem>
                        <SelectItem value="SAME_DAY">Same Day</SelectItem>
                        <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., Delivery in 3-5 business days"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="minDeliveryTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min Delivery Time</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value
                              ? Number(e.target.value)
                              : undefined;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxDeliveryTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Delivery Time</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value
                              ? Number(e.target.value)
                              : undefined;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timeUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time Unit</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select time unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="HOURS">Hours</SelectItem>
                          <SelectItem value="DAYS">Days</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Amount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="feeScope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Scope</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fee scope" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="DOMESTIC">Domestic</SelectItem>
                        <SelectItem value="INTERNATIONAL">
                          International
                        </SelectItem>
                        <SelectItem value="ANY">Any</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pickupServiceAreaId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pickup Service Area</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select pickup area" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {safeServiceAreas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name} ({area.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deliveryServiceAreaId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery Service Area</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select delivery area" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {safeServiceAreas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name} ({area.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>Active</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full">
                Create Speed Fee
              </Button>
            </>
          )}
        </form>
      </Form>
    );
  };

  const SpecialHandlingFeeForm = () => {
    const form = useForm<SpecialHandlingFeeCreateFormSchema>({
      resolver: zodResolver(specialHandlingFeeSchema),
      defaultValues: {
        isActive: true,
        feeScope: "DOMESTIC",
      },
    });

    const onSubmit = (data: SpecialHandlingFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Handling Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select handling type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="COOLING">Cooling</SelectItem>
                    <SelectItem value="FRAGILE">Fragile</SelectItem>
                    <SelectItem value="SIGNATURE_REQUIRED">
                      Signature Required
                    </SelectItem>
                    <SelectItem value="INSURANCE">Insurance</SelectItem>
                    <SelectItem value="LEAVE_AT_DOOR">Leave at Door</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="feeScope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Scope</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select fee scope" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="DOMESTIC">Domestic</SelectItem>
                    <SelectItem value="INTERNATIONAL">International</SelectItem>
                    <SelectItem value="ANY">Any</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Create Special Handling Fee
          </Button>
        </form>
      </Form>
    );
  };

  const PickupHandlingFeeForm = () => {
    const form = useForm<PickupHandlingFeeCreateFormSchema>({
      resolver: zodResolver(pickupHandlingFeeSchema),
      defaultValues: {
        isActive: true,
        feeScope: "DOMESTIC",
      },
    });

    const onSubmit = (data: PickupHandlingFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="feeScope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Scope</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select fee scope" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="DOMESTIC">Domestic</SelectItem>
                    <SelectItem value="INTERNATIONAL">International</SelectItem>
                    <SelectItem value="ANY">Any</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Create Pickup Handling Fee
          </Button>
        </form>
      </Form>
    );
  };

  const InternationalBaseFeeForm = () => {
    const form = useForm<InternationalBaseFeeCreateFormSchema>({
      resolver: zodResolver(internationalBaseFeeSchema),
      defaultValues: {
        isActive: true,
      },
    });

    const onSubmit = (data: InternationalBaseFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="originCountry"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Origin Country</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="destinationCountry"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Destination Country</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="basePrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Base Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Create International Base Fee
          </Button>
        </form>
      </Form>
    );
  };

  const InternationalSurchargeFeeForm = () => {
    const form = useForm<InternationalSurchargeFeeCreateFormSchema>({
      resolver: zodResolver(internationalSurchargeFeeSchema),
      defaultValues: {
        isActive: true,
      },
    });

    const onSubmit = (data: InternationalSurchargeFeeCreateFormSchema) => {
      createFeeMutation.mutate(data);
    };

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <CustomerTierSelect form={form} customerTiers={customerTiers} />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fee Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Create International Surcharge Fee
          </Button>
        </form>
      </Form>
    );
  };

  // Update CustomerTierSelect to accept customerTiers as a prop
  const CustomerTierSelect = <T extends { customerTierId: string }>({
    form,
    name = "customerTierId" as Path<T>,
    customerTiers,
  }: {
    form: UseFormReturn<T>;
    name?: Path<T>;
    customerTiers: CustomerTier[];
  }) => {
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Customer Tier</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer tier" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {customerTiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id}>
                    {tier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  // Update the renderFeeForm function to include new forms
  const renderFeeForm = () => {
    switch (selectedFeeType) {
      case "domestic-base":
        return <DomesticBaseFeeForm />;
      case "dimensional-weight":
        return <DimensionalWeightFeeForm />;
      case "speed":
        return <SpeedFeeForm />;
      case "special-handling":
        return <SpecialHandlingFeeForm />;
      case "pickup-handling":
        return <PickupHandlingFeeForm />;
      case "international-base":
        return <InternationalBaseFeeForm />;
      case "international-surcharge":
        return <InternationalSurchargeFeeForm />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Fee Type
        </label>
        <Select
          value={selectedFeeType}
          onValueChange={(value) => setSelectedFeeType(value as FeeType)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select fee type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="domestic-base">Domestic Base Fee</SelectItem>
            <SelectItem value="dimensional-weight">
              Dimensional Weight Fee
            </SelectItem>
            <SelectItem value="speed">Speed Fee</SelectItem>
            <SelectItem value="special-handling">
              Special Handling Fee
            </SelectItem>
            <SelectItem value="pickup-handling">Pickup Handling Fee</SelectItem>
            <SelectItem value="international-base">
              International Base Fee
            </SelectItem>
            <SelectItem value="international-surcharge">
              International Surcharge Fee
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {renderFeeForm()}
    </div>
  );
}
